import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Eye, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import { useDialerUsage } from '../api';
import type { DialerUsageDetail, DialerUsageRow } from '../types';

interface UsageModal {
  id: number;
  timestamp: string | number;
  details: DialerUsageDetail[];
}

export function DialerUsagePage() {
  const usage = useDialerUsage();
  const rows = usage.data ?? [];

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const [modal, setModal] = useState<UsageModal | null>(null);
  const [filter, setFilter] = useState('');

  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  }, []);

  const handleSearch = () => {
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }
    usage.mutate({
      startDate: startDate ? formatDate(startDate) : '',
      endDate: endDate ? formatDate(endDate) : '',
    });
  };

  const openDetails = (row: DialerUsageRow) => {
    try {
      const parsed = JSON.parse(row.dialerUsageJSON) as DialerUsageDetail[];
      const details = Array.isArray(parsed) ? parsed : [];
      setModal({ id: row.id, timestamp: row.timestamp, details });
      setFilter('');
    } catch {
      toast.error('Usage payload could not be parsed.');
    }
  };

  const columns = useMemo<ColumnDef<DialerUsageRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        size: 220,
        cell: ({ row }) => formatDateTime(row.original.timestamp),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 130,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" onClick={() => openDetails(row.original)}>
            <Eye className="h-4 w-4" />
            View details
          </Button>
        ),
      },
    ],
    [],
  );

  const filteredDetails = useMemo(() => {
    if (!modal) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return modal.details;
    return modal.details.filter((d) =>
      String(d.dialer_name ?? '').toLowerCase().includes(q),
    );
  }, [modal, filter]);

  return (
    <FillPage>
      <PageHeader
        title="Dialer Usage"
        description="Historical snapshots of per-dialer talk time, written daily by the backend."
        actions={
          <Button asChild variant="outline">
            <Link to="/dialer-list">
              <ArrowLeft className="h-4 w-4" />
              Back to dialers
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter snapshots</CardTitle>
          <CardDescription>
            Leave both dates empty to fetch the most recent snapshots.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick a start date"
                toDate={yesterday}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick an end date"
                toDate={yesterday}
              />
            </div>
            <Button onClick={handleSearch} disabled={usage.isPending}>
              {usage.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<DialerUsageRow, unknown>
        tableId="dialers:usage:v1"
        columns={columns}
        data={rows}
        isLoading={usage.isPending}
        searchPlaceholder="Filter snapshots…"
        emptyTitle="No snapshots loaded yet"
        emptyDescription="Pick a date range above and click Search."
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="dialer-usage.csv"
        pageSize={10}
      />

      <Dialog open={modal !== null} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Dialer usage details</DialogTitle>
            <DialogDescription>
              Snapshot {modal?.id} from {formatDateTime(modal?.timestamp)}.
            </DialogDescription>
          </DialogHeader>

          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by dialer name…"
          />

          <div className="grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
            {filteredDetails.length === 0 ? (
              <div className="col-span-full py-8 text-center text-sm text-muted-foreground">
                No dialers match this filter.
              </div>
            ) : (
              filteredDetails.map((d, i) => (
                <Card key={`${d.dialer_id}-${i}`} className="bg-muted/30">
                  <CardContent className="space-y-1.5 p-4">
                    <div className="text-base font-semibold text-foreground">
                      {d.dialer_name}
                    </div>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                      <dt className="text-muted-foreground">Trunk</dt>
                      <dd>{d.trunk_name || '—'}</dd>
                      <dt className="text-muted-foreground">Dialer ID</dt>
                      <dd className="font-mono text-xs">{d.dialer_id}</dd>
                      <dt className="text-muted-foreground">Total duration</dt>
                      <dd>{d.total_duration} sec</dd>
                      <dt className="text-muted-foreground">Date</dt>
                      <dd>{d.date}</dd>
                    </dl>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
